<template>
  <class-overview-card
    :class-data="item"
    :members="members"
    :normalized-times="normalizedTimes"
    :theme="theme"
    members-title="Alunos da turma"
    members-empty-text="Nenhum colega visível nesta turma no momento."
  >
    <template #members-content="{ members: sectionMembers }">
      <v-row v-if="sectionMembers.length" dense>
        <v-col
          v-for="(member, index) in sectionMembers"
          :key="member.id || `${member.email}-${index}`"
          cols="12"
          sm="6"
        >
          <class-member-card :student="member" :theme="theme" />
        </v-col>
      </v-row>

      <v-alert v-else type="info" text class="mb-0">
        Nenhum colega visível nesta turma no momento.
      </v-alert>

      <class-attendance-viewer
        :theme="theme"
        :loading="loadingAttendance"
        :month-options="attendanceMonthOptions"
        :selected-month-key="selectedAttendanceMonthKey"
        :events="attendanceEvents"
        :summary="attendanceSummary"
        title="Minha frequência"
        description="A presença é atualizada pelo professor da turma. Consulte aqui seu histórico mês a mês."
        @change-month="handleAttendanceMonthChange"
        @refresh="loadAttendance"
      />
    </template>
  </class-overview-card>
</template>

<script>
import ClassAttendanceViewer from "@/components/classes/ClassAttendanceViewer.vue";
import ClassMemberCard from "@/components/classes/ClassMemberCard.vue";
import ClassOverviewCard from "@/components/classes/ClassOverviewCard.vue";
import customerApi from "@/shared/pitchCustomerApi";
import { normalizeClassTimes } from "@/shared/classPresentation";
import { classThemes } from "@/shared/classThemes";

export default {
  name: "ClassView",

  components: {
    ClassAttendanceViewer,
    ClassMemberCard,
    ClassOverviewCard,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loadingAttendance: false,
      attendanceRequestToken: 0,
      attendanceAvailableMonths: [],
      attendanceEvents: [],
      selectedAttendanceMonthKey: null,
    };
  },

  watch: {
    "item.id": {
      immediate: true,
      async handler() {
        await this.loadAttendance();
      },
    },
  },

  computed: {
    members() {
      return Array.isArray(this.item.content) ? this.item.content : [];
    },
    attendanceMonthOptions() {
      return this.attendanceAvailableMonths.map((item) => ({
        text: item.label,
        value: item.key,
      }));
    },
    attendanceSummary() {
      const editableEvents = this.attendanceEvents.filter(
        (event) => !event.isFuture
      );
      const futureCount = this.attendanceEvents.length - editableEvents.length;
      const totalLessons = editableEvents.length;
      const presentCount = editableEvents.filter(
        (event) => +event.status === 1
      ).length;
      const absentCount = editableEvents.filter(
        (event) => +event.status === 2
      ).length;
      const pendingCount = Math.max(
        totalLessons - presentCount - absentCount,
        0
      );

      return {
        totalLessons,
        presentCount,
        absentCount,
        pendingCount,
        futureCount,
        presentPercentage: totalLessons
          ? Number(((presentCount / totalLessons) * 100).toFixed(1))
          : 0,
        absentPercentage: totalLessons
          ? Number(((absentCount / totalLessons) * 100).toFixed(1))
          : 0,
      };
    },
    normalizedTimes() {
      return normalizeClassTimes(this.item.weekDays, this.item.hours);
    },
    theme() {
      return classThemes.customer;
    },
  },

  methods: {
    resetAttendanceState() {
      this.attendanceRequestToken += 1;
      this.attendanceAvailableMonths = [];
      this.attendanceEvents = [];
      this.selectedAttendanceMonthKey = null;
    },
    buildAttendanceMonthKey(month, year) {
      if (!month || !year) {
        return null;
      }

      return `${year}-${String(month).padStart(2, "0")}`;
    },
    parseAttendanceMonthKey(monthKey) {
      if (!monthKey || !monthKey.includes("-")) {
        return null;
      }

      const [year, month] = monthKey.split("-").map(Number);

      if (!year || !month) {
        return null;
      }

      return { month, year };
    },
    applyAttendanceResponse(data) {
      this.attendanceAvailableMonths = Array.isArray(data?.availableMonths)
        ? data.availableMonths
        : [];
      this.selectedAttendanceMonthKey = this.buildAttendanceMonthKey(
        data?.month,
        data?.year
      );
      this.attendanceEvents = Array.isArray(data?.events) ? data.events : [];
    },
    async loadAttendance() {
      if (!this.item?.id) {
        this.resetAttendanceState();
        return;
      }

      const requestToken = ++this.attendanceRequestToken;
      this.loadingAttendance = true;

      try {
        const selectedPeriod = this.parseAttendanceMonthKey(
          this.selectedAttendanceMonthKey
        );
        const res = await customerApi.getStudyGroupAttendance(this.item.id, {
          month: selectedPeriod?.month,
          year: selectedPeriod?.year,
        });

        if (requestToken !== this.attendanceRequestToken) {
          return;
        }

        this.applyAttendanceResponse(res.data);
      } catch (error) {
        this.resetAttendanceState();
        this.$root.$emit("display-alert", {
          text: "Nao foi possivel carregar sua frequência nesta turma.",
          type: 1,
        });
      } finally {
        this.loadingAttendance = false;
      }
    },
    async handleAttendanceMonthChange(value) {
      this.selectedAttendanceMonthKey = value;
      await this.loadAttendance();
    },
  },
};
</script>
